import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent } from 'react'
import { ancoraDoClique, ancoraNoPonto } from './Popover.tsx'
import {
  agendaInfo,
  distribuirNaGrade,
  faixaDeHorario,
  horaNoDia,
  parseDayKey,
  posicaoNaGrade,
  vaiParaGradeDeHoras,
  type Ancora,
  type Compromisso,
} from './calendario.ts'

/** Altura de uma hora na grade (px). */
const HORA_PX = 48
/** A grade abre rolada para ~07:30 (design do calendário)... */
const HORA_INICIAL = 7.5
/** ...ou, com hoje à vista, para um pouco antes de agora. */
const HORAS_ANTES_DE_AGORA = 1.5
/** Chips na faixa "dia todo" antes do "+N mais" (Semana); no Dia cabem mais. */
const MAX_NA_FAIXA_SEMANA = 3
const MAX_NA_FAIXA_DIA = 6

const HORAS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`)

const DOW_FMT = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' })
const DIA_LONGO_FMT = new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })

const agoraEmMinutos = () => {
  const agora = new Date()
  return agora.getHours() * 60 + agora.getMinutes()
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Clique no vazio (fora de qualquer botão) da célula. */
const cliqueNoVazio = (event: MouseEvent<HTMLElement>) => !(event.target as HTMLElement).closest('button')

/**
 * Grade de horas das exibições Semana (7 colunas) e Dia (1 coluna), no
 * desenho do design (2026-10-31): cabeçalho com o dia da semana e o número
 * em círculo (hoje cheio), faixa "dia todo" com os eventos sem hora e, embaixo,
 * as 24 horas com os eventos em bloco na cor da agenda.
 *
 * Clicar num horário vazio cria um evento ali — na meia hora, quando o clique
 * foi na metade de baixo da hora. Clicar num evento abre o detalhe ao lado.
 */
export function GradeDeHoras({
  dias,
  hoje,
  selecionado,
  porDia,
  onAbrir,
  onNovo,
  onEscolherDia,
  foco,
}: {
  dias: readonly string[]
  hoje: string
  selecionado: string
  porDia: ReadonlyMap<string, readonly Compromisso[]>
  onAbrir: (c: Compromisso, ancora: Ancora) => void
  onNovo: (dia: string, hora: string | null, ancora: Ancora) => void
  onEscolherDia: (dia: string) => void
  /** Muda a cada clique em "Hoje": a grade volta para o horário de agora. */
  foco: number
}) {
  const rolagem = useRef<HTMLDivElement>(null)
  const [agora, setAgora] = useState(agoraEmMinutos)
  const [faixaAberta, setFaixaAberta] = useState(false)
  const maxNaFaixa = dias.length === 1 ? MAX_NA_FAIXA_DIA : MAX_NA_FAIXA_SEMANA

  const hojeAVista = dias.includes(hoje)
  useLayoutEffect(() => {
    if (!rolagem.current) return
    const hora = hojeAVista ? Math.max(0, agoraEmMinutos() / 60 - HORAS_ANTES_DE_AGORA) : HORA_INICIAL
    rolagem.current.scrollTop = hora * HORA_PX
  }, [foco, hojeAVista])

  useEffect(() => {
    const timer = setInterval(() => setAgora(agoraEmMinutos()), 60_000)
    return () => clearInterval(timer)
  }, [])

  const temFaixa = dias.some((dia) => (porDia.get(dia) ?? []).some((c) => !vaiParaGradeDeHoras(c)))
  const estilo = { '--cal-hg-cols': dias.length, '--cal-hg-hora': `${HORA_PX}px` } as CSSProperties

  return (
    <div className="cal-hg" style={estilo}>
      <div className="cal-hg__row cal-hg__head">
        <span className="cal-hg__gutter cal-hg__fuso" title="Horário de Brasília">
          GMT-3
        </span>
        {dias.map((dia) => {
          const data = parseDayKey(dia)
          const ehHoje = dia === hoje
          return (
            <div key={dia} className={`cal-hg__dayhead${ehHoje ? ' cal-hg__dayhead--hoje' : ''}`}>
              <span className="cal-hg__dow">{DOW_FMT.format(data).replace('.', '').toUpperCase()}</span>
              <button
                type="button"
                className={`cal-hg__num${dia === selecionado && !ehHoje ? ' cal-hg__num--sel' : ''}`}
                aria-current={ehHoje ? 'date' : undefined}
                aria-label={`Ver ${DIA_LONGO_FMT.format(data)}`}
                title="Ver dia"
                onClick={() => onEscolherDia(dia)}
              >
                {data.getDate()}
              </button>
            </div>
          )
        })}
      </div>

      {temFaixa ? (
        <div className="cal-hg__row cal-hg__allday">
          <span className="cal-hg__gutter cal-hg__gutter--label">dia todo</span>
          {dias.map((dia) => {
            const daFaixa = (porDia.get(dia) ?? []).filter((c) => !vaiParaGradeDeHoras(c))
            const sobra = daFaixa.length > maxNaFaixa
            const visiveis = faixaAberta || !sobra ? daFaixa : daFaixa.slice(0, maxNaFaixa - 1)
            return (
              <div
                key={dia}
                className="cal-hg__alldaycell"
                title="Clique no vazio para criar um evento de dia inteiro"
                onClick={(event) => {
                  if (cliqueNoVazio(event)) onNovo(dia, null, ancoraNoPonto(event.clientX, event.clientY))
                }}
              >
                {visiveis.map((c) => (
                  <Chip key={c.id} c={c} dia={dia} cheio onAbrir={onAbrir} />
                ))}
                {sobra ? (
                  <button type="button" className="cal-mais" onClick={() => setFaixaAberta((aberta) => !aberta)}>
                    {faixaAberta ? 'Mostrar menos' : `+${daFaixa.length - visiveis.length} mais`}
                  </button>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : null}

      <div className="cal-hg__scroll" ref={rolagem}>
        <div className="cal-hg__row cal-hg__body">
          <div className="cal-hg__hours" aria-hidden>
            {HORAS.map((hora, h) => (
              <span key={hora} className="cal-hg__hour">
                {h === 0 ? '' : hora}
              </span>
            ))}
          </div>

          {dias.map((dia) => {
            const comHora = distribuirNaGrade(
              (porDia.get(dia) ?? []).filter(vaiParaGradeDeHoras).map((c) => ({
                item: c,
                ...posicaoNaGrade(c.startTime!, c.endTime),
              })),
            )
            const rotuloDoDia = DIA_LONGO_FMT.format(parseDayKey(dia))
            const fimDeSemana = [0, 6].includes(parseDayKey(dia).getDay())
            return (
              <div key={dia} className={`cal-hg__col${fimDeSemana ? ' cal-hg__col--fds' : ''}${dia === hoje ? ' cal-hg__col--hoje' : ''}`}>
                {HORAS.map((hora, h) => (
                  <button
                    key={hora}
                    type="button"
                    className="cal-hg__slot"
                    aria-label={`Novo evento em ${rotuloDoDia} às ${hora}`}
                    onClick={(event) => {
                      const r = event.currentTarget.getBoundingClientRect()
                      // metade de baixo da hora: começa na meia hora
                      const meia = event.clientY - r.top > r.height / 2 ? 30 : 0
                      onNovo(dia, `${pad(h)}:${pad(meia)}`, ancoraNoPonto(event.clientX, event.clientY))
                    }}
                  />
                ))}

                {comHora.map(({ item: c, inicio, fim, coluna, colunas }) => {
                  const agenda = agendaInfo(c.agenda)
                  const altura = Math.max(((fim - inicio) / 60) * HORA_PX - 2, 18)
                  const concluido = Boolean(c.item?.concluido)
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={[
                        'cal-hg__ev',
                        altura < 40 ? 'cal-hg__ev--linha' : '',
                        concluido ? 'cal-hg__ev--feito' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={
                        {
                          top: (inicio / 60) * HORA_PX + 1,
                          height: altura,
                          left: `calc(${(coluna / colunas) * 100}% + 1px)`,
                          width: `calc(${100 / colunas}% - 6px)`,
                          '--cal-ev-cor': agenda.color,
                        } as CSSProperties
                      }
                      title={`${faixaDeHorario(c)} · ${c.title} — ${agenda.label}${concluido ? ' · concluído' : ''}`}
                      onClick={(event) => onAbrir(c, ancoraDoClique(event))}
                    >
                      <span className="cal-hg__evtitle">
                        {concluido ? <i className="fas fa-check-circle" aria-hidden /> : null}
                        {c.title}
                      </span>
                      <span className="cal-hg__evtime">{faixaDeHorario(c).replace(' — ', ' – ')}</span>
                    </button>
                  )
                })}

                {dia === hoje ? (
                  <span className="cal-hg__now" style={{ top: (agora / 60) * HORA_PX }} aria-hidden />
                ) : null}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Chip de compromisso (grade do mês, faixa "dia todo", popover do dia).
 *
 * `cheio`: bloco na cor da agenda com texto claro — sem hora (dia inteiro) ou
 * na faixa "dia todo". Senão, como no design: bolinha na cor da agenda, a hora
 * e o título. Item concluído: ícone de check + riscado (nunca só a cor).
 */
export function Chip({
  c,
  dia,
  cheio = false,
  onAbrir,
}: {
  c: Compromisso
  dia: string
  cheio?: boolean
  onAbrir: (c: Compromisso, ancora: Ancora) => void
}) {
  const agenda = agendaInfo(c.agenda)
  const concluido = Boolean(c.item?.concluido)
  const hora = horaNoDia(c, dia)
  const preenchido = (cheio || !hora) && !concluido
  const destino = c.item ? ` — ${c.item.code}` : ''
  return (
    <button
      type="button"
      className={['cal-chip', preenchido ? 'cal-chip--cheio' : '', concluido ? 'cal-chip--done' : ''].filter(Boolean).join(' ')}
      title={`${faixaDeHorario(c)} · ${c.title} (${agenda.label})${concluido ? ' · concluído' : ''}${destino}`}
      style={{ '--cal-ev-cor': agenda.color } as CSSProperties}
      onClick={(event) => onAbrir(c, ancoraDoClique(event))}
    >
      {concluido ? (
        <i className="fas fa-check-circle cal-chip__icone" aria-hidden />
      ) : preenchido ? (
        <i className={`${c.item ? (c.item.marco === 'inicio' ? 'fas fa-play-circle' : 'fas fa-flag') : agenda.icon} cal-chip__icone`} aria-hidden />
      ) : (
        <span className="cal-chip__dot" aria-hidden />
      )}
      {hora && !cheio ? <span className="cal-chip__time">{hora}</span> : null}
      <span className="cal-chip__title">{c.title}</span>
    </button>
  )
}
